<template lang="pug">
main.modal-wrapper
    svg-icon.close-modal.cursor(name='close' size="small" @click.native="closeModal()")

    div(v-if="isProcessing")
        ScreenLockLoader
    div.modal-main(v-else)

        header.modal-header
            sectionHeader(title="Share taxonomy")
    
        div.modal-body
            div(v-if="isLoading")
                spinner.l-flex-row--center.l-align-center(width="100" height="100")

            div(v-else-if="emptyCustomTaxonomies")
                p.no-item We could not find a taxonomy to share
                    |
                    br
                    | please create a taxonomy

            div(v-else-if="emptySharingAccountlist")
                p.no-item There are no accounts you can share taxonomies to
                    |
                    br
                    | please contact an administrator

            div.form-section(v-else)
                div.form-subsection.wide.form-subsection-taxo-list
                    div.form-label.title Select taxonomy ...
                    el-select(
                        id="selected-taxo"
                        name="selected taxo"
                        class="selected-selecting"
                        @change="onChangeSelectedTaxo"
                        v-model="taxoId")
                        el-option(
                            class="selected-options"
                            v-for="(customTaxo,index) in customTaxonomies"
                            :label="customTaxo.label"
                            :key="index" :value="customTaxo.id")
                            span(:title="customTaxo.label") {{ customTaxo.label }}

                div.form-subsection.wide
                    div.form-label.title ... To share with
                    multi-select(
                        :itemsList="formatedSharings" 
                        :previouslyActive="formatSharedTaxonomies" 
                        :currentTaxonomyItemsList="currentlySelectedTaxonomy"
                        :selectedTaxo="selectedTaxo"
                        @listChanged="updateSelectedAccounts"
                        @labelUpdated="updateSharingLabel"
                    )

        //- FOOTER
        footer.modal-footer(v-if="!emptySharingAccountlist && !emptyCustomTaxonomies")
            div.footer-content.l-flex-row--between
                button.btn.btn--medium.btn--round(@click="closeModal()") Cancel
                button.btn.btn--medium.btn--round.btn-rev.btn-save-audience(
                    :class="{'ui-disabled': disabledButton}"
                    @click="requestShareTaxonomy()") share              

</template>
<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { isEmpty, filter, toNumber, find, includes, isNil, difference, findIndex, isEqual } from 'lodash'

import helpers from '@/store/ui/manageTaxonomy/helpers'

import SectionHeader from '@/components/common/SectionHeader'
import MultiSelect from '@/components/common/MultiSelect'
import ScreenLockLoader from '@/components/common/ScreenLockLoader'

export default {
    name: 'TaxonomySharingForm',
    components: {
        SectionHeader,
        MultiSelect,
        ScreenLockLoader
    },
    mounted() {
        const slectedTaxoIdIndex = findIndex(this.customTaxonomies, taxo => taxo.id === this.selectedTaxoId)
        this.selectedTaxo = this.selectedSharingTaxonomy ? this.selectedSharingTaxonomy : this.customTaxonomies[slectedTaxoIdIndex]
        this.selectedAccounts = this.formatSharedTaxonomies

        //Retrieve Items if not already retrieved and assign it
        if (isEmpty(this.sharings)) {
            this.isLoading = true
            this.retrieveLinkedAccounts().then(() => {
                this.formatedSharings = this.formatData(this.sharings)
                this.isLoading = false
            })
        } else this.formatedSharings = this.formatData(this.sharings)
    },
    watch: {
        sharings: {
            handler: function(val) {
                this.formatedSharings = this.formatData(val)
            }
        },
        selectedTaxo: {
            handler: function() {
                this.selectedAccounts = this.formatSharedTaxonomies
            }
        },
        selectedAccounts: {
            handler: function(val, oldVal) {
                const updatedElement = difference(oldVal, val)
                if (updatedElement.length === 1 && includes(this.formatSharedTaxonomies, updatedElement[0])) {
                    const sharing = find(this.sharings, item => item.id === updatedElement[0])
                    this.setAddedNotification({
                        type: 'warning',
                        message: '“The active sharing to the account: ' + sharing.id + ' – ' + sharing.label + ' will be removed upon saving”'
                    })
                }
            },
            deep: true
        }
    },
    data() {
        return {
            selectedTaxo: null,
            formatedSharings: [],
            selectedAccounts: [],
            isLoading: false,
            updatedLabels: [],
            isProcessing: false,
            taxoId: null
        }
    },
    methods: {
        ...mapMutations('ui/manageTaxonomy', ['setDisplaySharingTaxonomyModal', 'setSelectedSharingTaxonomy']),
        ...mapActions('data/settings', ['retrieveLinkedAccounts']),
        ...mapActions('ui/manageTaxonomy', ['shareTaxonomy', 'retrieveTaxonomySharingStatus']),
        ...mapMutations('notifications', ['setAddedNotification']),

        closeModal() {
            this.setDisplaySharingTaxonomyModal({ display: false })
            this.setSelectedSharingTaxonomy({ taxo: null })
            this.selectedAccounts = []
        },
        formatData(data) {
            return filter(data, item => item.wam_custom_taxonomy_sharing === 'active')
        },
        updateSelectedAccounts(accountsList) {
            this.selectedAccounts = accountsList
        },

        /**
         * Execute request to send API call to activate or deactivate a taxo
         * Only send the accounts that were not previously active to active and if they are active on the UI
         * Deactivate accounts that are in store but no longer active in the UI
         */
        requestShareTaxonomy() {
            const sharings = []
            this.isProcessing = true

            // Gathering taxos for whom we want to active the sharing
            for (const sharing of this.selectedAccounts) {
                // We only add items that were not currently active
                const alreadyActiveSharedTaxo = find(
                    this.sharedTaxonomies[this.selectedTaxo.id],
                    item => item.product_account_id === sharing && item.status === 'active'
                )
                const updatedLabel = find(this.updatedLabels, item => item.accountId === sharing)

                // Activating sharings that have not been activated
                if (!alreadyActiveSharedTaxo || updatedLabel) {
                    const item = {}
                    const desiredSharing = find(this.formatedSharings, account => account.id === sharing)
                    item.sharing_account_id = toNumber(desiredSharing.id)
                    item.label = updatedLabel ? updatedLabel.label : this.selectedTaxo.label
                    item.status = 'active'
                    sharings.push(item)
                }
            }

            //Gathering taxos form whom we want to stop the sharing
            for (const sharing of this.sharingsToDelete) {
                const item = {}
                item.sharing_account_id = sharing.product_account_id
                item.label = sharing.label
                item.status = 'inactive'
                sharings.push(item)
            }

            const params = {
                wam_custom_taxonomy_id: toNumber(this.selectedTaxo.id),
                sharings: sharings
            }
            this.shareTaxonomy(params).then(() => (this.isProcessing = false))
        },
        updateSharingLabel(accountId, label, taxoId) {
            const alreadyUpdated = find(this.updatedLabels, item => item.accountId === taxoId)
            if (alreadyUpdated) {
                const index = findIndex(this.updatedLabels, item => item.accountId === taxoId)
                this.updatedLabels.splice(index, 1)
            }
            this.updatedLabels.push({
                accountId: accountId,
                taxoId: taxoId,
                label: label
            })
        },
        onChangeSelectedTaxo(id) {
            this.selectedTaxo = this.customTaxonomies.find(taxo => taxo.id === id)
        }
    },
    computed: {
        ...mapState('data/customTaxonomies', {
            customTaxonomies: state => state.customTaxonomies
        }),
        ...mapState('ui/manageTaxonomy', {
            selectedSharingTaxonomy: state => state.selectedSharingTaxonomy,
            selectedTaxoId: state => state.selectedTaxoId
        }),
        ...mapState('data/settings', {
            sharings: state => state.sharing.taxonomies,
            sharedTaxonomies: state => state.sharing.sharedTaxonomies
        }),
        disabledButton() {
            const currentlySelectedTaxonomy = this.currentlySelectedTaxonomy ? this.currentlySelectedTaxonomy : []
            return (
                this.errors.items.length > 0 ||
                isEmpty(this.selectedTaxo) ||
                (isEmpty(this.sharingsToDelete) &&
                    isEmpty(this.updatedLabels) &&
                    isEqual(
                        this.selectedAccounts.slice().sort(),
                        currentlySelectedTaxonomy
                            .filter(item => item.status === 'active')
                            .map(item => item.product_account_id)
                            .sort()
                    ))
            )
        },
        emptySharingAccountlist() {
            return isEmpty(this.formatedSharings)
        },

        emptyCustomTaxonomies() {
            return this.customTaxonomies.length < 1
        },
        /**
         * Return an array of taxoID for every active items in store
         * @returns {Array}
         */
        formatSharedTaxonomies() {
            const active = []
            if (!this.selectedTaxo) return []
            if (this.sharedTaxonomies[this.selectedTaxo.id]) {
                return helpers.formatSharedTaxonomies(this.sharedTaxonomies[this.selectedTaxo.id])
            }
            return active
        },

        /**
         * Return an array of every active items in store that no longer are active in the UI
         * @returns {Array}
         */
        sharingsToDelete() {
            if (!isNil(this.selectedTaxo)) {
                return filter(
                    this.sharedTaxonomies[this.selectedTaxo.id],
                    item => !includes(this.selectedAccounts, item.product_account_id) && item.status === 'active'
                )
            }
            return []
        },

        /**
         * Returns the selected taxonomy and its sharings
         * @returns {Array}
         */
        currentlySelectedTaxonomy() {
            if (this.selectedTaxo) return this.sharedTaxonomies[this.selectedTaxo.id]
            return []
        }
    }
}
</script>
